import { Component, register } from '../Component';
import { theme } from '../theme';
import { glassSurface } from './glassStyle';

export interface PopupProps {
  open: boolean;
  withBackground?: boolean;
  /** Clicking the backdrop calls onClose. Defaults to true. */
  backgroundCanClose?: boolean;
  /**
   * When true (the default) the popup is promoted to the browser's top layer
   * (via the Popover API) so it renders above everything and escapes any
   * ancestor stacking/overflow context — transforms, `overflow: hidden`, low
   * `z-index`, etc. Crucially it stays exactly where it is in the DOM, so slots
   * and the owner's scoped styles keep working. Set to false to render inline.
   */
  portal?: boolean;
  onClose?: () => void;
}

@register('x-popup')
export class Popup extends Component<PopupProps> {
  // Whether the host is acting as a top-layer popover this session.
  private _usePopover = false;

  constructor() {
    super({ props: { withBackground: true, portal: true } });
  }

  init() {
    // The Popover API promotes an element to the top layer without moving it in
    // the DOM. That escapes ancestor transforms/overflow (which trap
    // position: fixed) while preserving slot projection and the owner's scoped
    // styles — both of which break if the element is relocated instead.
    this._usePopover =
      this.props.portal !== false && typeof this.showPopover === 'function';

    if (this._usePopover) this.setAttribute('popover', 'manual');

    return () => {
      const handleClick = (e: MouseEvent) => {
        if ((e.target as HTMLElement).classList.contains('wrapper')) {
          if (this.props.backgroundCanClose !== false) {
            this.props.onClose && this.props.onClose();
          }
        }
      };

      // Move the rim light to the cursor.
      const handlePointerMove = (e: PointerEvent) => {
        const modal = e.currentTarget as HTMLElement;
        const rect = modal.getBoundingClientRect();
        modal.style.setProperty('--mx', `${e.clientX - rect.left}px`);
        modal.style.setProperty('--my', `${e.clientY - rect.top}px`);
      };

      this.toggleAttribute('open', this.props.open);
      this._syncPopover();

      return (
        <div
          class={`wrapper popup ${this.props.open ? 'visible' : ''} ${
            this.props.withBackground ? 'withBackground' : ''
          }`}
          onclick={handleClick}>
          <div class="modal" onpointermove={handlePointerMove}>
            <slot></slot>
          </div>
        </div>
      );
    };
  }

  // Keep the top-layer state in sync with the open prop. Guarded so it is a
  // no-op when unsupported or when the host isn't connected yet (e.g. in tests).
  private _syncPopover() {
    if (!this._usePopover || !this.isConnected) return;

    const isOpen = this.matches(':popover-open');
    try {
      if (this.props.open && !isOpen) this.showPopover();
      else if (!this.props.open && isOpen) this.hidePopover();
    } catch {
      /* showPopover throws if state already changed under us; ignore. */
    }
  }

  getStyle() {
    return StyledPopup;
  }
}

const StyledPopup = cssStylesheet(css`
  /* Inline fallback (portal disabled / Popover API unsupported): the host's
     visibility is driven by the open attribute. */
  :host(:not([popover])) {
    display: none;
  }
  :host(:not([popover])[open]) {
    display: initial;
  }

  /* Top-layer popover: strip the UA popover box styling so we own the layout.
     The UA hides it while closed (:not(:popover-open)). */
  :host([popover]) {
    margin: 0;
    padding: 0;
    border: 0;
    background: none;
    overflow: visible;
    /* The UA sheet also sets color: CanvasText on [popover]. That beats the
       inherited page color and cascades into every descendant's shadow tree,
       so restore the theme colour here rather than in each child component. */
    color: ${theme?.colors.onSurface};
  }

  :host,
  :host > div {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }

  /* The host spans the whole viewport (and sits in the top layer when acting
     as a popover), so it must never hit-test itself — only the backdrop and
     modal opt back in. Otherwise it swallows clicks meant for content below,
     even with withBackground=false. */
  :host {
    pointer-events: none;
  }

  :host > div {
    pointer-events: none;
    background: none;
    opacity: 0;
    visibility: hidden;
    transform: scale(1.1);
    transition: visibility 0s linear 0.25s, opacity 0.25s 0s, transform 0.25s;
    z-index: 1;
  }

  :host > .withBackground {
    pointer-events: all;
    background: rgba(0, 0, 0, 0.5);
  }

  :host > .visible {
    opacity: 1;
    visibility: visible;
    transform: scale(1);
    transition: visibility 0s linear 0s, opacity 0.25s 0s, transform 0.25s;
  }

  /* Frosted glass: the scene behind shows through, blurred. */
  .modal {
    pointer-events: all;
    padding: 1rem;
    ${glassSurface()}
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 14px;
    min-width: 300px;
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }

  /* The rim: a faint 1px edge, brightest nearest the cursor (--mx, --my).
     The mask cuts out the middle. */
  .modal::before,
  .modal::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
  }
  .modal::before {
    padding: 1px;
    background: radial-gradient(
        240px circle at var(--mx, 50%) var(--my, 0%),
        rgba(255, 255, 255, 0.95),
        transparent 70%
      ),
      linear-gradient(${theme?.colors.glassEdge}, ${theme?.colors.glassEdge});
    mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    mask-composite: exclude;
  }
  /* A soft sheen across the glass, around the cursor. It sits under the
     content: .modal is a stacking context, because of its transform. */
  .modal::after {
    z-index: -1;
    background: radial-gradient(
      500px circle at var(--mx, 50%) var(--my, 0%),
      rgba(255, 255, 255, 0.09),
      transparent 60%
    );
  }
`);
